
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { usePendingInvitations, useRespondToInvitation } from "@/hooks/useCollaboration";
import { getPermissionLabel } from "@/utils/collaboration";
import { Check, X, Users } from "lucide-react";

export const CollaborationInvitations = () => {
  const { data: invitations = [], isLoading, error } = usePendingInvitations();
  const respondToInvitation = useRespondToInvitation();

  if (isLoading) {
    return (
      <Card className="mb-6">
        <CardContent className="p-4">
          <p className="text-muted-foreground">Loading collaboration invitations...</p>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="mb-6">
        <CardContent className="p-4">
          <p className="text-destructive">Error loading collaboration invitations</p>
        </CardContent>
      </Card>
    );
  }

  if (invitations.length === 0) {
    return null;
  }

  const handleAccept = (invitationId: string) => {
    respondToInvitation.mutate({ invitationId, status: 'accepted' });
  };

  const handleReject = (invitationId: string) => {
    respondToInvitation.mutate({ invitationId, status: 'rejected' });
  };

  return (
    <Card className="mb-6">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Users className="h-5 w-5" />
          Collaboration Invitations ({invitations.length})
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {invitations.map((invitation) => (
          <div key={invitation.id} className="flex items-center justify-between p-4 border rounded-lg">
            <div className="flex items-center gap-3">
              <Avatar className="h-10 w-10">
                <AvatarImage src={invitation.inviter.avatar_url} />
                <AvatarFallback>
                  {invitation.inviter.full_name?.charAt(0) || invitation.inviter.email.charAt(0)}
                </AvatarFallback>
              </Avatar>
              <div>
                <p className="font-medium">
                  {invitation.inviter.full_name || invitation.inviter.email} invited you to collaborate on
                </p>
                <p className="text-sm text-muted-foreground font-semibold">
                  {invitation.test.title}
                </p>
                <div className="flex items-center gap-2 mt-1">
                  <Badge variant="outline">
                    {getPermissionLabel(invitation.permission)}
                  </Badge>
                  {invitation.inviter.organization && (
                    <span className="text-xs text-muted-foreground">
                      from {invitation.inviter.organization}
                    </span>
                  )}
                </div>
              </div>
            </div>
            <div className="flex gap-2">
              <Button
                size="sm"
                onClick={() => handleAccept(invitation.id)}
                disabled={respondToInvitation.isPending}
                className="gap-1"
              >
                <Check className="h-4 w-4" />
                Accept
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleReject(invitation.id)}
                disabled={respondToInvitation.isPending}
                className="gap-1"
              >
                <X className="h-4 w-4" />
                Decline
              </Button>
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
};
